import type { ApolloError } from "@apollo/client";
import type { GraphQLErrors } from "@apollo/client/errors";
import { useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import { Toast } from "~/client/components/shared/toast";
import type { CreateSearchFilterSetMutation } from "~/client/generated/schema";

const errorMessageIds = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "INVALID_INPUT": "searchFilterSet.save.error",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "TOO_MANY_ITEMS": "searchFilterSet.save.over_max_count_error",
};

type ErrorCode = keyof typeof errorMessageIds;

const detectErrorCode = (errors: GraphQLErrors): ErrorCode => {
  const codes = errors.map(({ extensions }) => extensions.code) as Array<string>;
  const knownCode = codes.find((code) => Object.keys(errorMessageIds).includes(code));
  if (!knownCode) {
    return "INVALID_INPUT";
  }
  return knownCode as ErrorCode;
};

interface Props {
  data: CreateSearchFilterSetMutation | null | undefined;
  loading: boolean;
  error: ApolloError | undefined;
}
export const SearchFilterSetSavingFeedback: React.FC<Props> = ({ data, loading, error }) => {
  const intl = useIntl();
  const toastRef = useRef<Toast | null>(null);

  useEffect(() => {
    if (!loading) {
      if (data) {
        toastRef.current?.showToast({ success: intl.formatMessage({ id: "searchFilterSet.save.success" }) });
      } else if (error) {
        const id = errorMessageIds[detectErrorCode(error.graphQLErrors)];
        toastRef.current?.showToast({ error: intl.formatMessage({ id }) });
      }
    }
  }, [loading, data, error, intl]);

  return <Toast ref={toastRef} />;
};
